import { AfterViewInit, Component, OnDestroy, Optional, ViewChild } from '@angular/core';
import { VehicleService } from 'src/app/management/service/vehicle.service';
import { Vehicle } from 'src/app/vehicles/model/vehicle';
import { MenuElement } from 'src/app/shared/model/menu-element';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ReceiverType } from '../../../model/receiver-type';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { Receiver } from '../../../model/receiver';
import { ChannelService } from '../../../service/channel.service';
import { DeviceType } from 'src/app/vehicles/model/device-type';
import { StoreService } from 'src/app/store/service/store.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { UserSettingsService } from 'src/app/settings/service/user-settings.service';
import { DialogService } from 'src/app/common/dialog/dialog.service';
import { filterData } from 'src/app/shared/util/data-util';
import { GPSFrame } from 'src/app/vehicles/model/gps-frame';
import { Operator } from 'src/app/shared/model/operator';
import { ChatMapBridgeService } from 'src/app/common/service/chat-map-bridge.service';
import { Subject } from 'rxjs';
import { ChatService } from 'src/app/top-bar/service/chat.service';
import { LatLng } from 'leaflet';

@Component({
  selector: 'app-chat-selector',
  templateUrl: './chat-selector.component.html',
  styleUrls: ['./chat-selector.component.scss'],
  providers: [VehicleService]
})
export class ChatSelectorComponent implements AfterViewInit, OnDestroy {

  readonly vehiclesMode: string = 'vehicles';
  readonly linesMode: string = 'lines';
  readonly allMode: string = 'all';
  readonly chatsMode: string = 'chats';

  @ViewChild('menuElementGroup') menuElementGroup: MatButtonToggleGroup;
  @ViewChild('vehiclesSelector') vehiclesSelector: MatSelectionList;

  vehicles: Vehicle[] = [];
  lines: string[] = [];
  menuElements: MenuElement[];
  activeElement: MenuElement;
  mapSelectionEnabled: boolean = false;
  mapSelections: Vehicle[] = [];
  mapEnabled: boolean;
  private unsubscribe = new Subject();
  private drawingEnableSub = new Subject();
  private selectedPoints: LatLng[] = [];

  constructor(private vehicleService: VehicleService, private channelService: ChannelService, private storeService: StoreService,
    @Optional() private mapService: ChatMapBridgeService, private chatService: ChatService, private router: Router, private settings: UserSettingsService,
    private dialog: DialogService) {
    this.menuElements = [
      {
        name: 'toAll',
        icon: 'to_all',
        route: this.allMode
      },
      {
        name: 'chats',
        icon: 'messages',
        route: this.chatsMode
      },
      {
        name: 'selectVehicles',
        icon: 'vehicles_on',
        route: this.vehiclesMode
      },
      {
        name: 'selectLines',
        icon: 'icon_points_with_line',
        route: this.linesMode
      }
    ];
    this.mapEnabled = settings.shouldShowLiveVehiclesMap();
    this.chatService.visibiltyStream$.pipe(takeUntil(this.unsubscribe), filter(v => v === false)).subscribe({
      next: () => {
        if (this.mapSelectionEnabled) this.stopDraw();
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();

  }

  ngAfterViewInit(): void {
    this.vehicleService.fetchVehicles().subscribe(vehicles => this.vehicles = vehicles);
    this.vehicleService.fetchLines().subscribe(lines => this.lines = lines);
    this.menuElementGroup.valueChange.pipe(takeUntil(this.unsubscribe)).subscribe(element => {
      this.activeElement = element;
      if (this.activeElement?.route !== this.vehiclesMode) {
        this.stopDraw();
      }
      this.emitMode();
    });
    this.channelService.pushDefaultReceivers();
    this.mapService.disconnectSub().pipe(takeUntil(this.unsubscribe)).subscribe({
      next: () => this.mapSelectionEnabled = false
    });
  }

  toggleDrawFromMap(): void {
    if (this.mapEnabled) {
      if (this.router.url === '/vehicles') {
        this.startDraw();
      } else {
        this.dialog.openConfirmationDialog('  ', 'route-to-vehicles-map').afterClosed().pipe(
          filter(v => v === true)
        ).subscribe({
          next: () => {
            void this.router.navigate(['/vehicles']).then(() => this.startDraw());
          }
        });
      }
    }
  }

  private startDraw(): void {
    this.drawingEnableSub.next(this.mapSelectionEnabled = true);
    this.mapService.draw().pipe(
      switchMap(points => this.storeService.getAll<GPSFrame>(DeviceType.GPS_FRAME).pipe(filter(data => data != null), take(1), tap(gpsFrame => {
        if (points?.length > 0) {
            this.mapSelections = filterData<GPSFrame>({
              operator: Operator.contains,
              fieldName: 'area',
              value: `POLYGON((${points.map(point => `${point.lng} ${point.lat}`).join(',')}))`
            }, gpsFrame).map(frame => frame.vehicle);
        }
      }
      ))),
      takeUntil(this.drawingEnableSub.pipe(filter(v => v === false)))
    ).subscribe();
  }

  confirmDraw(): void {
    const uniqueValues = Array.from(new Set([
        ...this.mapSelections.map(veh => veh.sideNumber),
        ...this.vehiclesSelector.selectedOptions.selected.map(o => o.value)
      ]));
    this.writeSelected(uniqueValues);
    this.stopDraw();
  }

  stopDraw(): void {
    this.mapSelectionEnabled = false;
    this.mapSelections = [];
    this.mapService.stopDraw();
    this.drawingEnableSub.next(this.mapSelectionEnabled = false);
  }

  toggleAll(event: MatCheckboxChange): void {
    if (this.activeElement?.route === this.vehiclesMode) {
      const selected = event.checked ? this.vehicles.map(veh => veh.sideNumber) : [];
      this.writeSelected(selected);
    }
  }

  resetDraw(): void {
    const mapSideNumbers = this.mapSelections.map(v => v.sideNumber);
    const withoutMapSelections = this.vehiclesSelector.selectedOptions.selected.map(o => o.value).filter((v: string) => !mapSideNumbers.includes(v));
    this.stopDraw();
    this.writeSelected(withoutMapSelections);
  }

  channelClicked(channels: string[]): void {
    const receivers: Receiver[] = this.createReceivers(channels);
    this.channelService.pushReceivers(receivers);
  }

  getChannelsFromOptions(event: MatListOption[]): string[] {
    return event.map(option => option.value);
  }

  getUnreadForChannel(channel: string): number {
    return this.channelService.getUnreadForChannel(channel);
  }

  getConversation(): string[] {
    return this.vehicles.map(veh => veh.sideNumber);
  }

  private writeSelected(channels: string[]): void {
    this.vehiclesSelector.writeValue(channels);
    this.channelClicked(channels);
  }

  private createReceivers(channels: string[]): Receiver[] {
    const receivers: Receiver[] = [];
    channels.forEach(name => {
      const receiver: Receiver = Object.assign(new Receiver(), {
        name,
        receiverType: this.getReceiverTypeByMode(),
        vehId: this.vehicles.find(veh => veh.sideNumber === name)?.id
      });
      if (!receivers.includes(receiver))
        receivers.push(receiver);
    });
    return receivers;
  }

  private getReceiverTypeByMode(): ReceiverType {
    switch (this.activeElement.route) {
      case this.vehiclesMode: return ReceiverType.VEHICLE;
      case this.chatsMode: return ReceiverType.VEHICLE;
      case this.allMode: return ReceiverType.ALL;
      case this.linesMode: return ReceiverType.LINE;
    }
  }


  private emitMode(): void {
    if (this.activeElement) {
      switch (this.activeElement.route) {
        case this.allMode: {
          this.channelService.pushDefaultReceivers();
          break;
        }
        default: {
          this.channelService.pushReceivers([]);
          break;
        }
      }
    }
  }
}
